import { useEffect, useState, useRef } from "react";

let _boltSeq = 0;

/**
 * Reusable Lightning-Bolt loader overlay with live percentage counter.
 * Matches the previous all-screen loader visual (teal gradient-fill bolt + % badge),
 * but scoped to any position:relative container.
 *
 * @param {number} [progress] - 0-100 percentage. If omitted, ticks smoothly from 15% to ~95%.
 * @param {string} [label] - Status text below the percentage.
 * @param {number|string} [minHeight] - Optional container minimum height fallback.
 */
const BoltLoader = ({
  progress,
  label = "Loading data\u2026",
  downloadInfo = null,
  minHeight = 220,
}) => {
  const idRef = useRef(null);
  if (!idRef.current) {
    idRef.current = `bolt-grad-${++_boltSeq}`;
  }
  const id = idRef.current;

  // Auto-ticker for percentage if progress is not passed as an explicit number
  const [internalProgress, setInternalProgress] = useState(() =>
    typeof progress === "number" ? progress : 15
  );

  // Continuous elapsed time counter ticking every 100ms
  const [elapsedSeconds, setElapsedSeconds] = useState("0.0");
  const mountTimeRef = useRef(Date.now());

  useEffect(() => {
    mountTimeRef.current = Date.now();
    setElapsedSeconds("0.0");

    const timer = setInterval(() => {
      const start = downloadInfo?.startTime || mountTimeRef.current;
      const sec = Math.max(0, (Date.now() - start) / 1000).toFixed(1);
      setElapsedSeconds(sec);
    }, 100);

    return () => clearInterval(timer);
  }, [downloadInfo?.startTime]);

  const EXPECTED_DURATION = 25.0;
  const elapsed = parseFloat(elapsedSeconds) || 0;
  const remaining = Math.max(0, Math.round(EXPECTED_DURATION - elapsed));

  // Paced smoothly over 25 seconds average: reaches ~95% around 24s
  let pct = 5;
  if (typeof progress === "number") {
    pct = progress;
  } else if (downloadInfo && typeof downloadInfo.percent === "number") {
    pct = downloadInfo.percent;
  } else if (downloadInfo && downloadInfo.total && downloadInfo.loaded) {
    pct = Math.min(100, Math.round((downloadInfo.loaded / downloadInfo.total) * 100));
  } else {
    const ratio = Math.min(1, elapsed / EXPECTED_DURATION);
    const curve = 1 - (1 - ratio) * (1 - ratio);
    pct = Math.min(95, Math.round(5 + curve * 90));
  }
  pct = Math.min(100, Math.max(5, pct));

  const hasDownloadData = downloadInfo && downloadInfo.loaded > 0;
  const loadedKb = hasDownloadData ? (downloadInfo.loaded / 1024).toFixed(1) : "0.0";
  const totalKb = downloadInfo?.total ? (downloadInfo.total / 1024).toFixed(1) : null;
  const speedKbps = downloadInfo?.speedKbps || "0.0";

  const expectedKb = downloadInfo?.expectedKb || "61.7";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 15,
        backgroundColor: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "inherit",
        minHeight,
        pointerEvents: "none",
      }}
      role="status"
      aria-live="polite"
    >
      {/* Lightning bolt with animated pulse glow */}
      <div className="historical-lightning-loader" aria-hidden="true">
        <svg
          viewBox="0 0 160 247"
          focusable="false"
          style={{ width: 68, height: 110 }}
        >
          <defs>
            <linearGradient
              id={id}
              x1="80"
              y1="241.5"
              x2="80"
              y2="5.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset={`${pct}%`} stopColor="#009F8D" stopOpacity="1" />
              <stop offset={`${pct}%`} stopColor="#009F8D" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#009F8D" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            className="historical-bolt-shape"
            fill={`url(#${id})`}
            stroke="#009F8D"
            strokeWidth="8"
            strokeLinejoin="round"
            d="M137.641 5.5H60.1789L5.5 135.194H67.9251L32.8394 241.5L154.5 86.718H98.9098L137.641 5.5Z"
          />
        </svg>
      </div>

      {/* Percentage number and label */}
      <div className="historical-loader-percent-badge">
        <span className="historical-loader-percent-number">
          {Math.round(pct)}%
        </span>
        <span className="historical-loader-percent-label">{label}</span>
      </div>

      {/* Real-time Download Progress Details calibrated to 25s average */}
      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          fontWeight: 600,
          color: "#0f766e",
          backgroundColor: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: 8,
          padding: "6px 14px",
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "#10b981",
            boxShadow: "0 0 6px rgba(16, 185, 129, 0.7)",
          }}
        />
        <span>
          {hasDownloadData ? (
            <>
              <span>
                {totalKb
                  ? `${loadedKb} KB / ${totalKb} KB (${downloadInfo.percent || Math.round((loadedKb / totalKb) * 100)}%)`
                  : `Downloaded: ${loadedKb} KB`}
              </span>
              {parseFloat(speedKbps) > 0 && (
                <span style={{ color: "#0d9488", marginLeft: 8, fontWeight: 700 }}>
                  • {speedKbps} KB/s
                </span>
              )}
            </>
          ) : elapsed < EXPECTED_DURATION ? (
            <>
              <span>
                {elapsed < 2.0
                  ? `Connecting to server (~${expectedKb} KB)…`
                  : elapsed < 12.0
                  ? `Querying database (~${expectedKb} KB)…`
                  : `Aggregating metrics (~${expectedKb} KB)…`}
              </span>
              <span style={{ color: "#0d9488", marginLeft: 8, fontWeight: 700 }}>
                ~{remaining}s remaining
              </span>
            </>
          ) : (
            <span style={{ color: "#0d9488", fontWeight: 700 }}>
              Finalizing response (~${expectedKb} KB)…
            </span>
          )}
        </span>
        <span
          style={{
            color: "#64748b",
            fontWeight: 500,
            fontVariantNumeric: "tabular-nums",
            borderLeft: "1px solid #cbd5e1",
            paddingLeft: 8,
          }}
        >
          {elapsedSeconds}s / ~25s
        </span>
      </div>
    </div>
  );
};

export default BoltLoader;
